import React from 'react';
import { Link } from 'react-router-dom';
import { CreditCard } from 'lucide-react';
import { useCardlessTrialNotice } from '../../hooks/useCardlessTrialNotice';

/**
 * Persistent header affordance for tenants with no card on file. The top-of-app notice
 * strip can be dismissed; this cannot — it stays in the navbar for as long as a card is
 * actually needed, so the path to adding one never disappears. Deliberately small and
 * quiet (icon-only until there's room for the label) to stay out of the way.
 *
 * Keys off `needsCard` rather than the banner's `state` so dismissing the strip has no
 * effect here. Deep-links straight into the add-card modal on the billing page.
 */
const AddCardNavButton: React.FC = () => {
  const { needsCard, daysLeft } = useCardlessTrialNotice();

  if (!needsCard) return null;

  const hint =
    daysLeft === null
      ? 'Add a payment method to keep your account'
      : daysLeft <= 0
        ? 'Your trial ends today — add a payment method to keep your account'
        : daysLeft === 1
          ? 'Your trial ends tomorrow — add a payment method to keep your account'
          : `Your trial ends in ${daysLeft} days — add a payment method to keep your account`;

  return (
    <Link
      to="/billing"
      state={{ openPaymentMethods: true }}
      title={hint}
      aria-label={hint}
      className="
        hidden sm:inline-flex items-center gap-1.5 h-8 px-2.5
        rounded-lg border border-warning-200 bg-warning-50
        text-13 font-medium text-warning-700
        hover:bg-warning-100 hover:border-warning-300
        transition-colors
        focus:outline-none focus-visible:ring-2 focus-visible:ring-warning-500/40 focus-visible:ring-offset-1
      "
    >
      <CreditCard className="h-3.5 w-3.5 flex-none" aria-hidden="true" />
      <span className="hidden lg:inline">Add card</span>
    </Link>
  );
};

export default AddCardNavButton;
